import React, { useEffect, useMemo, useState } from 'react';

const normalizeApiBase = (rawValue, fallback) => {
  const cleaned = (rawValue || '')
    .trim()
    .replace(/\s+#.*$/, '')
    .replace(/^['\"]|['\"]$/g, '')
    .replace(/;$/, '')
    .replace(/\/+$/, '');

  if (!cleaned) {
    return fallback;
  }

  const withProtocol = /^https?:\/\//i.test(cleaned) ? cleaned : `http://${cleaned}`;

  try {
    return new URL(withProtocol).toString().replace(/\/$/, '');
  } catch {
    return fallback;
  }
};

const buildApiUrl = (base, path) => {
  const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
  try {
    return new URL(normalizedPath, `${base}/`).toString();
  } catch {
    const safeBase = String(base || GROUP_REG_FALLBACK)
      .trim()
      .replace(/^['\"]|['\"]$/g, '')
      .replace(/\/+$/, '');
    return `${safeBase}/${normalizedPath}`;
  }
};

const GROUP_REG_FALLBACK = 'http://localhost:8086';
const CONFERENCE_FALLBACK = 'http://localhost:8080/conference';
const GROUP_REG_API = process.env.NODE_ENV === 'development'
  ? GROUP_REG_FALLBACK
  : normalizeApiBase(process.env.REACT_APP_GROUP_REGISTRATION_API_URL, GROUP_REG_FALLBACK);
const CONFERENCE_API = process.env.NODE_ENV === 'development'
  ? CONFERENCE_FALLBACK
  : normalizeApiBase(process.env.REACT_APP_CONFERENCE_API_URL, CONFERENCE_FALLBACK);

const initialFormState = {
  conference: '',
  corporateName: '',
  totalRegistrations: '',
  totalAmount: '',
  discountApplied: '',
  paymentReference: '',
  bannerUrl: '',
  paymentStatus: 'PENDING'
};

const ConferenceGroupManagement = () => {
  const [groups, setGroups] = useState([]);
  const [conferences, setConferences] = useState([]);
  const [conferenceFilter, setConferenceFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [formData, setFormData] = useState(initialFormState);

  const sortedGroups = useMemo(
    () => [...groups].sort((a, b) => (b.id || 0) - (a.id || 0)),
    [groups]
  );

  const visibleGroups = useMemo(() => {
    if (!conferenceFilter) {
      return sortedGroups;
    }
    return sortedGroups.filter((group) => group.conference === conferenceFilter);
  }, [sortedGroups, conferenceFilter]);

  const conferenceOptions = useMemo(
    () => conferences.map((conference) => conference.name).filter(Boolean),
    [conferences]
  );

  useEffect(() => {
    fetchGroups();
    fetchConferences();
  }, []);

  const showAlert = (message, type = 'success') => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 4500);
  };

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const response = await fetch(buildApiUrl(GROUP_REG_API, '/group-registration/groups'));
      if (!response.ok) {
        throw new Error('Failed to load conference groups');
      }
      const data = await response.json();
      setGroups(Array.isArray(data) ? data : []);
    } catch (error) {
      if (error instanceof SyntaxError) {
        try {
          const fallbackResponse = await fetch(buildApiUrl(GROUP_REG_FALLBACK, '/group-registration/groups'));
          if (fallbackResponse.ok) {
            const fallbackData = await fallbackResponse.json();
            setGroups(Array.isArray(fallbackData) ? fallbackData : []);
            showAlert('Loaded conference groups using fallback API URL', 'success');
            return;
          }
        } catch (fallbackError) {
          console.error('Fallback conference groups fetch failed:', fallbackError);
        }
      }
      console.error('Failed to fetch conference groups:', error);
      showAlert('Failed to load conference groups', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchConferences = async () => {
    try {
      const response = await fetch(buildApiUrl(CONFERENCE_API, '/active'));
      if (!response.ok) {
        throw new Error('Failed to load conferences');
      }
      const data = await response.json();
      setConferences(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch conferences:', error);
      showAlert('Failed to load conferences', 'error');
    }
  };

  const openCreateModal = () => {
    setEditingGroup(null);
    setFormData({
      ...initialFormState,
      conference: conferenceFilter || ''
    });
    setShowModal(true);
  };

  const openEditModal = (group) => {
    setEditingGroup(group);
    setFormData({
      conference: group.conference || '',
      corporateName: group.corporateName || '',
      totalRegistrations: group.totalRegistrations ?? '',
      totalAmount: group.totalAmount ?? '',
      discountApplied: group.discountApplied ?? '',
      paymentReference: group.paymentReference || '',
      bannerUrl: group.bannerUrl || '',
      paymentStatus: group.paymentStatus || 'PENDING'
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingGroup(null);
    setFormData(initialFormState);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const parseNullableNumber = (value) => {
    if (value === '' || value === null || value === undefined) {
      return null;
    }
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.conference.trim() || !formData.corporateName.trim()) {
      showAlert('Conference and corporate name are required', 'error');
      return;
    }

    const createPayload = {
      conference: formData.conference.trim(),
      corporateName: formData.corporateName.trim(),
      totalRegistrations: parseNullableNumber(formData.totalRegistrations),
      totalAmount: parseNullableNumber(formData.totalAmount),
      discountApplied: parseNullableNumber(formData.discountApplied),
      paymentReference: formData.paymentReference.trim() || null,
      bannerUrl: formData.bannerUrl.trim() || null
    };

    try {
      if (!editingGroup) {
        const response = await fetch(buildApiUrl(GROUP_REG_API, '/group-registration/groups'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(createPayload)
        });

        if (!response.ok) {
          const text = await response.text();
          throw new Error(text || 'Create failed');
        }

        showAlert('Conference group created successfully', 'success');
      } else {
        // Prefer full update endpoint if available.
        const updateResponse = await fetch(buildApiUrl(GROUP_REG_API, `/group-registration/groups/${editingGroup.id}`), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...createPayload, paymentStatus: formData.paymentStatus })
        });

        if (updateResponse.ok) {
          showAlert('Conference group updated successfully', 'success');
        } else {
          // Fallback to payment-status endpoint currently available in backend.
          const paymentResponse = await fetch(
            buildApiUrl(GROUP_REG_API, `/group-registration/groups/${editingGroup.id}/payment-status`),
            {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                paymentStatus: formData.paymentStatus,
                paymentReference: formData.paymentReference.trim() || null
              })
            }
          );

          if (!paymentResponse.ok) {
            const text = await paymentResponse.text();
            throw new Error(text || 'Update failed');
          }

          showAlert('Payment status updated. Full update endpoint not available yet.', 'success');
        }
      }

      closeModal();
      fetchGroups();
    } catch (error) {
      console.error('Failed to save conference group:', error);
      showAlert(error.message || 'Failed to save conference group', 'error');
    }
  };

  const handleDelete = async (groupId) => {
    if (!window.confirm('Delete this conference group?')) {
      return;
    }

    try {
      const response = await fetch(buildApiUrl(GROUP_REG_API, `/group-registration/groups/${groupId}`), {
        method: 'DELETE'
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Delete failed');
      }

      showAlert('Conference group deleted successfully', 'success');
      fetchGroups();
    } catch (error) {
      console.error('Failed to delete conference group:', error);
      showAlert(error.message || 'Delete endpoint not available yet', 'error');
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <div>
          <h2>Conference Group Management</h2>
          <p style={{ color: '#666', marginTop: '6px' }}>
            Create, update and delete records from conference_group.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={conferenceFilter} onChange={(e) => setConferenceFilter(e.target.value)} style={inputStyle}>
            <option value="">All Conferences</option>
            {conferenceOptions.map((conference) => (
              <option key={conference} value={conference}>
                {conference}
              </option>
            ))}
          </select>
          <button
            onClick={openCreateModal}
            style={{
              padding: '10px 16px',
              border: 'none',
              borderRadius: '4px',
              background: '#0d6efd',
              color: '#fff',
              cursor: 'pointer',
              fontWeight: 600
            }}
          >
            Add Conference Group
          </button>
        </div>
      </div>

      {alert && (
        <div
          style={{
            marginBottom: '16px',
            padding: '12px',
            borderRadius: '6px',
            background: alert.type === 'error' ? '#f8d7da' : '#d1e7dd',
            color: alert.type === 'error' ? '#842029' : '#0f5132',
            border: `1px solid ${alert.type === 'error' ? '#f5c2c7' : '#badbcc'}`
          }}
        >
          {alert.message}
        </div>
      )}

      <div style={{ background: '#fff', borderRadius: '8px', overflowX: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: '#f7f9fc' }}>
            <tr>
              <th style={th}>ID</th>
              <th style={th}>Conference</th>
              <th style={th}>Corporate Name</th>
              <th style={th}>Total Registrations</th>
              <th style={th}>Total Amount</th>
              <th style={th}>Discount</th>
              <th style={th}>Payment Status</th>
              <th style={th}>Payment Reference</th>
              <th style={th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && visibleGroups.length === 0 && (
              <tr>
                <td style={emptyCell} colSpan={9}>No conference groups found.</td>
              </tr>
            )}
            {loading && (
              <tr>
                <td style={emptyCell} colSpan={9}>Loading conference groups...</td>
              </tr>
            )}
            {visibleGroups.map((group) => (
              <tr key={group.id}>
                <td style={td}>{group.id}</td>
                <td style={td}>{group.conference}</td>
                <td style={td}>{group.corporateName}</td>
                <td style={td}>{group.totalRegistrations ?? '-'}</td>
                <td style={td}>{group.totalAmount ?? '-'}</td>
                <td style={td}>{group.discountApplied ?? '-'}</td>
                <td style={td}>{group.paymentStatus || '-'}</td>
                <td style={td}>{group.paymentReference || '-'}</td>
                <td style={td}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => openEditModal(group)} style={secondaryBtn}>Edit</button>
                    <button onClick={() => handleDelete(group.id)} style={dangerBtn}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <h3 style={{ marginTop: 0 }}>{editingGroup ? 'Edit Conference Group' : 'Create Conference Group'}</h3>
            <form onSubmit={handleSubmit}>
              <div style={formGrid}>
                <label style={labelStyle}>Conference
                  {conferenceOptions.length > 0 ? (
                    <select name="conference" value={formData.conference} onChange={handleInputChange} style={inputStyle} required>
                      <option value="">Select Conference</option>
                      {conferenceOptions.map((conference) => (
                        <option key={conference} value={conference}>
                          {conference}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      name="conference"
                      value={formData.conference}
                      onChange={handleInputChange}
                      style={inputStyle}
                      placeholder="Enter conference name"
                      required
                    />
                  )}
                </label>
                <label style={labelStyle}>Corporate Name
                  <input name="corporateName" value={formData.corporateName} onChange={handleInputChange} style={inputStyle} required />
                </label>
                <label style={labelStyle}>Total Registrations
                  <input name="totalRegistrations" value={formData.totalRegistrations} onChange={handleInputChange} style={inputStyle} type="number" min="0" />
                </label>
                <label style={labelStyle}>Total Amount
                  <input name="totalAmount" value={formData.totalAmount} onChange={handleInputChange} style={inputStyle} type="number" step="0.01" min="0" />
                </label>
                <label style={labelStyle}>Discount Applied
                  <input name="discountApplied" value={formData.discountApplied} onChange={handleInputChange} style={inputStyle} type="number" step="0.01" min="0" />
                </label>
                <label style={labelStyle}>Payment Reference
                  <input name="paymentReference" value={formData.paymentReference} onChange={handleInputChange} style={inputStyle} />
                </label>
                <label style={{...labelStyle, gridColumn: '1 / -1'}}>Banner URL
                  <input
                    type="url"
                    name="bannerUrl"
                    value={formData.bannerUrl}
                    onChange={handleInputChange}
                    style={inputStyle}
                    placeholder="https://example.com/banner.jpg"
                  />
                </label>
                <label style={labelStyle}>Payment Status
                  <select name="paymentStatus" value={formData.paymentStatus} onChange={handleInputChange} style={inputStyle}>
                    <option value="PENDING">PENDING</option>
                    <option value="COMPLETED">COMPLETED</option>
                    <option value="FAILED">FAILED</option>
                  </select>
                </label>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '14px' }}>
                <button type="button" onClick={closeModal} style={secondaryBtn}>Cancel</button>
                <button type="submit" style={primaryBtn}>{editingGroup ? 'Save Changes' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const th = {
  textAlign: 'left',
  padding: '12px',
  fontSize: '13px',
  borderBottom: '1px solid #e9ecef'
};

const td = {
  padding: '12px',
  borderBottom: '1px solid #f1f3f5',
  fontSize: '14px'
};

const emptyCell = {
  ...td,
  textAlign: 'center',
  color: '#666'
};

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0, 0, 0, 0.35)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000
};

const modalStyle = {
  width: 'min(820px, 92vw)',
  background: '#fff',
  borderRadius: '8px',
  padding: '20px'
};

const formGrid = {
  display: 'grid',
  gap: '12px',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))'
};

const labelStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  fontSize: '13px',
  color: '#495057'
};

const inputStyle = {
  padding: '10px',
  border: '1px solid #ced4da',
  borderRadius: '4px',
  fontSize: '14px'
};

const primaryBtn = {
  padding: '10px 14px',
  border: 'none',
  borderRadius: '4px',
  background: '#0d6efd',
  color: '#fff',
  fontWeight: 600,
  cursor: 'pointer'
};

const secondaryBtn = {
  padding: '8px 12px',
  border: '1px solid #d0d7de',
  borderRadius: '4px',
  background: '#fff',
  color: '#212529',
  cursor: 'pointer'
};

const dangerBtn = {
  padding: '8px 12px',
  border: 'none',
  borderRadius: '4px',
  background: '#dc3545',
  color: '#fff',
  cursor: 'pointer'
};

export default ConferenceGroupManagement;
